/* tslint:disable */
/* eslint-disable */
/* Mocked in-memory implementation of ReportsClient for local development and UI wiring. */

import { CancelToken } from 'axios';
import {
  SuccessResponse_1OfGetReportsResponse,
  FileResponse,
  AutomatedTask,
  RequestItemHistoryReportRequest,
  RequestTheoryActualReportRequest,
  RequestStockCountsVsUsageReportRequest,
  StockOnHandReportRequest,
  WasteReportRequest,
  ItemActivityReportRequest,
  TransfersReportRequest,
  SuccessResponse_1OfList_1OfDateTime,
  ItemTimeOutRequest,
  MadeInStoreReportRequest,
  CostOfSalesRequest,
  StockDaysRequest,
  ActualTheorySummary,
  InvoiceReconciliationRequest,
  StockCountSummaryReportRequest,
  ProductMarginReportRequest,
  CreditPurchaseReportRequest,
  ExtendedStockCountReportRequest,
  DcShortageReportRequest,
  ConstrainedItemReportRequest,
  ItemStockOutRequest,
  ReceiptsReportRequest,
  CheckReportRequest,
  RequestCOGsReportRequest,
  ItemUsageReportRequest,
  InventoryExtensionReportRequest,
  GiveawaysSamplesReportRequest,
} from '../../types';

/* ---- Helper Functions ---- */

function delay(ms: number = 300): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function successResponse<T>(data: T): { data: T; success: boolean; message: string } {
  return { data, success: true, message: 'Success' };
}

function mockFileResponse(fileName: string): FileResponse {
  return {
    data: new Blob(['mock report data'], { type: 'application/pdf' }),
    fileName,
  } as FileResponse;
}

/* ---- Mock Data ---- */

const mockReports = [
  { id: 1, name: 'Stock Variance Report', description: 'Weekly stock variance analysis' },
  { id: 2, name: 'Transaction Report', description: 'Daily transaction summary' },
  { id: 3, name: 'Purchase Order Report', description: 'PO delivery tracking' },
];

const mockScheduledReports = [
  {
    id: 1,
    name: 'Weekly Stock Variance',
    reportType: 'StockVariance',
    frequency: 'Weekly',
    recipients: ['manager@store.com'],
    storeNumber: 'STORE001',
    lastRun: '2025-12-09',
    nextRun: '2025-12-16',
  },
  {
    id: 2,
    name: 'Daily Transactions',
    reportType: 'Transactions',
    frequency: 'Daily',
    recipients: ['admin@store.com'],
    storeNumber: 'STORE001',
    lastRun: '2025-12-11',
    nextRun: '2025-12-12',
  },
];

/* ---- Mocked Client Implementation ---- */

import { IReportsClient } from '../../interfaces';
export class ReportsClientMocked implements IReportsClient {
  constructor(baseUrl?: string) {}

  async get3(cancelToken?: CancelToken): Promise<SuccessResponse_1OfGetReportsResponse> {
    await delay();
    return successResponse({ reports: mockReports }) as SuccessResponse_1OfGetReportsResponse;
  }

  async getPurchaseOrderDetails(poId: number, cancelToken?: CancelToken): Promise<FileResponse> {
    await delay();
    return mockFileResponse(`purchase_order_${poId}.pdf`);
  }

  async getReport(id: number, cancelToken?: CancelToken): Promise<FileResponse> {
    await delay();
    return mockFileResponse(`report_${id}.pdf`);
  }

  async getStockCountReportById(stockCountId: number, cancelToken?: CancelToken): Promise<FileResponse> {
    await delay();
    return mockFileResponse(`stock_count_${stockCountId}.pdf`);
  }

  async getExtendedStockCheckReportByIdAsPdf(stockCheckId: number, cancelToken?: CancelToken): Promise<FileResponse> {
    await delay();
    return mockFileResponse(`extended_stock_check_${stockCheckId}.pdf`);
  }

  async getExtendedStockCheckReportByIdAsExcel(stockCheckId: number, cancelToken?: CancelToken): Promise<FileResponse> {
    await delay();
    return {
      data: new Blob(['mock excel data'], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      fileName: `extended_stock_check_${stockCheckId}.xlsx`,
    } as FileResponse;
  }

  async getWasteCheckSheetReportById(wasteCheckSheetId: number, cancelToken?: CancelToken): Promise<FileResponse> {
    await delay();
    return mockFileResponse(`waste_checksheet_${wasteCheckSheetId}.pdf`);
  }

  async getStockVariance(storeNumber?: string, fromDate?: string, toDate?: string, cancelToken?: CancelToken): Promise<FileResponse> {
    await delay();
    return mockFileResponse('stock_variance_report.pdf');
  }

  async getStockVarianceV2(storeNumber?: string, fromDate?: string, toDate?: string, cancelToken?: CancelToken): Promise<FileResponse> {
    await delay();
    return mockFileResponse('stock_variance_report_v2.pdf');
  }

  async getOperationalDays(storeNumber?: string, fromDate?: string, toDate?: string, cancelToken?: CancelToken): Promise<FileResponse> {
    await delay();
    return mockFileResponse('operational_days_report.pdf');
  }

  async getProducts(storeNumber?: string, cancelToken?: CancelToken): Promise<FileResponse> {
    await delay();
    return mockFileResponse('products_report.pdf');
  }

  async getProductsV2(storeNumber?: string, cancelToken?: CancelToken): Promise<FileResponse> {
    await delay();
    return mockFileResponse('products_report_v2.pdf');
  }

  async getTransactions(storeNumber?: string, fromDate?: string, toDate?: string, cancelToken?: CancelToken): Promise<FileResponse> {
    await delay();
    return mockFileResponse('transactions_report.pdf');
  }

  async getTransfer(storeNumber?: string, transferId?: number, cancelToken?: CancelToken): Promise<FileResponse> {
    await delay();
    return mockFileResponse(`transfer_${transferId}.pdf`);
  }

  async getPurchaseOrderDeliveries(storeNumber?: string, fromDate?: string, toDate?: string, cancelToken?: CancelToken): Promise<FileResponse> {
    await delay();
    return mockFileResponse('po_deliveries_report.pdf');
  }

  async getTheoreticalConsumption(storeNumber?: string, fromDate?: string, toDate?: string, cancelToken?: CancelToken): Promise<FileResponse> {
    await delay();
    return mockFileResponse('theoretical_consumption_report.pdf');
  }

  async scheduledreportsGET(storeNumber: string, cancelToken?: CancelToken): Promise<{ data: typeof mockScheduledReports }> {
    await delay();
    const reports = mockScheduledReports.filter((r) => r.storeNumber === storeNumber);
    return successResponse(reports) as { data: typeof mockScheduledReports };
  }

  async scheduledreportsPOST(body?: AutomatedTask, cancelToken?: CancelToken): Promise<void> {
    await delay();
    if (body) {
      mockScheduledReports.push({
        id: mockScheduledReports.length + 1,
        name: body.name || 'New Report',
        reportType: body.taskType || 'Custom',
        frequency: 'Weekly',
        recipients: body.additionalEmails?.split(',') || [],
        storeNumber: 'STORE001',
        lastRun: '',
        nextRun: '',
      });
    }
  }

  async scheduledreportsGET2(id: number, cancelToken?: CancelToken): Promise<{ data: (typeof mockScheduledReports)[0] }> {
    await delay();
    const report = mockScheduledReports.find((r) => r.id === id) || mockScheduledReports[0];
    return successResponse(report) as { data: (typeof mockScheduledReports)[0] };
  }

  async scheduledreportsDELETE(id: number, cancelToken?: CancelToken): Promise<void> {
    await delay();
    const index = mockScheduledReports.findIndex((r) => r.id === id);
    if (index > -1) {
      mockScheduledReports.splice(index, 1);
    }
  }

  async scheduledreportsPUT(id: number, body?: AutomatedTask, cancelToken?: CancelToken): Promise<void> {
    await delay();
    const report = mockScheduledReports.find((r) => r.id === id);
    if (report && body) {
      report.name = body.name || report.name;
    }
  }

  // ====== Auto-generated stub methods ======















    async getItemHistoryReportById(body?: RequestItemHistoryReportRequest | undefined, cancelToken?: CancelToken | undefined): Promise<FileResponse> {
        throw new Error('Method getItemHistoryReportById not implemented in mocked client');
    }

    async getTheoryActualReport(body?: RequestTheoryActualReportRequest | undefined, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method getTheoryActualReport not implemented in mocked client');
    }

    async getStockCountsVsUsageReport(body?: RequestStockCountsVsUsageReportRequest | undefined, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method getStockCountsVsUsageReport not implemented in mocked client');
    }

    async getStockOnHandReport(body?: StockOnHandReportRequest | undefined, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method getStockOnHandReport not implemented in mocked client');
    }

    async getWasteReport(body?: WasteReportRequest | undefined, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method getWasteReport not implemented in mocked client');
    }

    async getItemActivityReport(body?: ItemActivityReportRequest | undefined, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method getItemActivityReport not implemented in mocked client');
    }

    async getTransfersReport(body?: TransfersReportRequest | undefined, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method getTransfersReport not implemented in mocked client');
    }

    async getAvailableReportingDates(storeNumbers?: string | undefined, districtIds?: string | undefined, allStores?: boolean | undefined, numberOfWeeks?: number | undefined, cancelToken?: CancelToken | undefined): Promise<SuccessResponse_1OfList_1OfDateTime> {
        throw new Error('Method getAvailableReportingDates not implemented in mocked client');
    }

    async getItemTimeOutReport(body?: ItemTimeOutRequest | undefined, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method getItemTimeOutReport not implemented in mocked client');
    }

    async getMadeInStoreReport(body?: MadeInStoreReportRequest | undefined, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method getMadeInStoreReport not implemented in mocked client');
    }

    async getCostOfSalesReport(body?: CostOfSalesRequest | undefined, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method getCostOfSalesReport not implemented in mocked client');
    }

    async getStockDaysReport(body?: StockDaysRequest | undefined, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method getStockDaysReport not implemented in mocked client');
    }

    async getActualTheorySummaries(from?: string | undefined, to?: string | undefined, userLevel?: string | undefined, cancelToken?: CancelToken | undefined): Promise<ActualTheorySummary[]> {
        throw new Error('Method getActualTheorySummaries not implemented in mocked client');
    }

    async getInvoiceReconciliationReport(body?: InvoiceReconciliationRequest | undefined, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method getInvoiceReconciliationReport not implemented in mocked client');
    }

    async getStockCountSummaryReport(body?: StockCountSummaryReportRequest | undefined, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method getStockCountSummaryReport not implemented in mocked client');
    }

    async getProductMarginReport(body?: ProductMarginReportRequest | undefined, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method getProductMarginReport not implemented in mocked client');
    }

    async getCreditPurchaseReport(body?: CreditPurchaseReportRequest | undefined, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method getCreditPurchaseReport not implemented in mocked client');
    }

    async getExtendedStockCountReport(body?: ExtendedStockCountReportRequest | undefined, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method getExtendedStockCountReport not implemented in mocked client');
    }

    async getDcShortageReport(body?: DcShortageReportRequest | undefined, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method getDcShortageReport not implemented in mocked client');
    }

    async getConstrainedItemReport(body?: ConstrainedItemReportRequest | undefined, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method getConstrainedItemReport not implemented in mocked client');
    }

    async getItemStockOutReport(body?: ItemStockOutRequest | undefined, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method getItemStockOutReport not implemented in mocked client');
    }

    async getReceiptsReport(body?: ReceiptsReportRequest | undefined, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method getReceiptsReport not implemented in mocked client');
    }

    async getCheckReport(body?: CheckReportRequest | undefined, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method getCheckReport not implemented in mocked client');
    }

    async getCOGsReport(body?: RequestCOGsReportRequest | undefined, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method getCOGsReport not implemented in mocked client');
    }

    async getItemUsageReport(body?: ItemUsageReportRequest | undefined, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method getItemUsageReport not implemented in mocked client');
    }

    async getInventoryExtensionReport(body?: InventoryExtensionReportRequest | undefined, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method getInventoryExtensionReport not implemented in mocked client');
    }

    async getGiveawaysSamplesReport(body?: GiveawaysSamplesReportRequest | undefined, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method getGiveawaysSamplesReport not implemented in mocked client');
    }

    async getUserReports(userId: number, cancelToken?: CancelToken | undefined): Promise<SuccessResponse_1OfGetReportsResponse> {
        throw new Error('Method getUserReports not implemented in mocked client');
    }

    async getUserReport(userId: number, id: number, cancelToken?: CancelToken | undefined): Promise<FileResponse> {
        throw new Error('Method getUserReport not implemented in mocked client');
    }

    async getPoDetailsAsPdf(poId: number, cancelToken?: CancelToken | undefined): Promise<FileResponse> {
        throw new Error('Method getPoDetailsAsPdf not implemented in mocked client');
    }

    async testGenerateReport(cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method testGenerateReport not implemented in mocked client');
    }

    async testGenerateIntegration(cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method testGenerateIntegration not implemented in mocked client');
    }

    async testIntegrationTransactionFacts(cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method testIntegrationTransactionFacts not implemented in mocked client');
    }

    async testGintWaves(gintWave: number, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method testGintWaves not implemented in mocked client');
    }

}
